import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Radio,
    FormControlLabel,
    Paper,
    Divider,
    CircularProgress
} from '@mui/material';
import TrackTree from '../stations/components/TrackTree';
import { EXAMPLE_WORKFLOWS } from './exampleWorkflowData';

interface ExampleWorkflowsModalProps {
    open: boolean;
    onClose: () => void;
    onCreateExamples: (selectedWorkflows: string[]) => Promise<void>;
}

export default function ExampleWorkflowsModal({ open, onClose, onCreateExamples }: ExampleWorkflowsModalProps) {
    const [selected, setSelected] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    const handleSelect = (id: string) => {
        setSelected(id);
    };

    const handleCreate = async () => {
        if (!selected) return;
        setIsCreating(true);
        try {
            await onCreateExamples([selected]);
            setSelected(null);
            onClose();
        } finally {
            setIsCreating(false);
        }
    };

    const handleClose = () => {
        if (!isCreating) {
            setSelected(null);
            onClose();
        }
    };

    const workflows = Object.values(EXAMPLE_WORKFLOWS);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Select an Example Route</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select an example route to create. This will create stations and a route you can use.
                </Typography>

                {workflows.map((workflow, index) => (
                    <React.Fragment key={workflow.id}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: selected === workflow.id ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                cursor: 'pointer',
                                '&:hover': { borderColor: 'primary.light' }
                            }}
                            onClick={() => handleSelect(workflow.id)}
                        >
                            <FormControlLabel
                                control={
                                    <Radio
                                        checked={selected === workflow.id}
                                        onChange={() => handleSelect(workflow.id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={500}>
                                            {workflow.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {workflow.description}
                                        </Typography>
                                    </Box>
                                }
                            />
                            <Box sx={{ ml: { xs: 1, sm: 4 }, mt: 1, overflow: 'auto' }}>
                                <TrackTree
                                    track={workflow.previewTrack}
                                    stops={workflow.previewStops}
                                    allStations={[]}
                                    height={workflow.previewStops.length * 32}
                                />
                            </Box>
                        </Paper>
                        {index < workflows.length - 1 && <Divider sx={{ my: 2 }} />}
                    </React.Fragment>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isCreating}>
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    color="primary"
                    disabled={!selected || isCreating}
                    startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {isCreating ? 'Creating...' : 'Create Example Route'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
